import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { updates } from '../data/mock';
import { Link } from 'react-router-dom';
import { Calendar, ArrowRight, Bell } from 'lucide-react';
import { Button } from '../components/ui/button';

const Updates = () => {
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const getCategoryColor = (category) => {
    const colors = {
      'Tax': 'bg-blue-100 text-blue-700',
      'GST': 'bg-orange-100 text-orange-700',
      'Business': 'bg-emerald-100 text-emerald-700',
      'Compliance': 'bg-purple-100 text-purple-700'
    };
    return colors[category] || 'bg-gray-100 text-gray-700';
  };

  return (
    <div>
      {/* Page Header */}
      <section className="bg-gradient-to-br from-emerald-800 to-emerald-900 py-20">
        <div className="container mx-auto px-4">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">Latest Updates</h1>
            <p className="text-emerald-200 text-lg max-w-2xl mx-auto">
              Stay informed with the latest news, regulatory changes, and important deadlines
            </p>
            <div className="flex items-center justify-center gap-2 mt-6 text-sm">
              <Link to="/" className="text-emerald-300 hover:text-white transition-colors">Home</Link>
              <span className="text-emerald-500">/</span>
              <span className="text-white">Updates</span>
            </div>
          </div>
        </div>
      </section>

      {/* Updates List */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {/* Alert Banner */}
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6 mb-12 flex items-start gap-4">
              <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Bell className="text-emerald-600" size={24} />
              </div>
              <div>
                <h3 className="font-semibold text-emerald-800 mb-1">Stay Updated!</h3>
                <p className="text-emerald-700">
                  Subscribe to our updates to never miss important deadlines and regulatory changes. 
                  Contact us to get added to our notification list.
                </p>
              </div>
            </div>

            {/* Updates Cards */}
            <div className="space-y-6">
              {updates.map((update, index) => (
                <Card key={update.id} className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden">
                  <CardContent className="p-0">
                    <div className="flex flex-col md:flex-row">
                      <div className="md:w-48 bg-gradient-to-br from-emerald-600 to-emerald-700 p-6 flex flex-col justify-center items-center text-white">
                        <Calendar size={24} className="mb-2" />
                        <span className="text-sm text-emerald-200">Published</span>
                        <span className="font-semibold text-center">{formatDate(update.date)}</span>
                      </div>
                      <div className="flex-1 p-6">
                        <div className="flex items-center gap-3 mb-3">
                          <Badge className={getCategoryColor(update.category)}>
                            {update.category}
                          </Badge>
                          {index === 0 && (
                            <Badge className="bg-red-100 text-red-700">New</Badge>
                          )}
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 mb-3">{update.title}</h3>
                        <p className="text-gray-600 leading-relaxed">{update.description}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* No More Updates Message */}
            <div className="text-center mt-12 py-8 border-t border-gray-200">
              <p className="text-gray-500">You're all caught up! Check back later for more updates.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-emerald-700">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">Have Questions About These Updates?</h2>
          <p className="text-emerald-200 mb-8 max-w-xl mx-auto">
            Our experts are here to help you understand how these changes affect your business.
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-white text-emerald-700 hover:bg-emerald-50">
              Contact Our Experts
              <ArrowRight className="ml-2" size={18} />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Updates;
